import './styles.css';
import { CONFIG } from './config';
import { Narration } from './narration';
import { Activity } from './activity';
import { CountingActivity } from './counting-activity';
import { FuelActivity } from './fuel-activity';
import { getCachedNarrationLines, getCachedVoiceManifest, initBootGate } from './boot-gate';
import { installServiceWorker } from './sw-register';

/**
 * Boot flow:
 *   LOAD (show overlay, fetch all required assets, confirm SW precache)
 *   -> READY (hide overlay, show start button)
 *   -> STARTED (audio unlock + intro + activity)
 *
 * The start button click is the single user gesture that unlocks audio. The
 * first audible play() is narration.play('intro'), invoked synchronously from
 * the gesture handler tick (PRP AC-02). No play() is attempted before this
 * gesture.
 */

const loadingOverlay = document.getElementById('loading-overlay') as HTMLElement;
const retryButton = document.getElementById('retry-button') as HTMLButtonElement;
const startScreen = document.getElementById('start-screen') as HTMLElement;
const startButton = document.getElementById('start-button') as HTMLButtonElement;
const activityScreen = document.getElementById('activity-screen') as HTMLElement;

const scene = document.getElementById('scene') as HTMLElement;
const orbits = document.getElementById('orbits') as unknown as SVGSVGElement;
const silhouettes = document.getElementById('silhouettes') as HTMLElement;
const planetsLayer = document.getElementById('planets-layer') as HTMLElement;
const dock = document.getElementById('dock') as HTMLElement;
const caption = document.getElementById('caption') as HTMLElement;
const sun = document.getElementById('sun') as HTMLImageElement;
const muteButton = document.getElementById('mute-button') as HTMLButtonElement;
const replayInstructionButton = document.getElementById(
  'replay-instruction-button',
) as HTMLButtonElement;
const completionOverlay = document.getElementById('completion-overlay') as HTMLElement;
const replayButton = document.getElementById('replay-button') as HTMLButtonElement;
const muteIcon = document.getElementById('mute-icon') as HTMLImageElement;
const nextLevelButton = document.getElementById('next-level-button') as HTMLButtonElement;

const countingScreen = document.getElementById('counting-screen') as HTMLElement;
const countStarsLayer = document.getElementById('count-stars-layer') as HTMLElement;
const countNumeral = document.getElementById('count-numeral') as HTMLElement;
const countTotal = document.getElementById('count-total') as HTMLElement;
const countRocket = document.getElementById('count-rocket') as HTMLElement;
const countCaption = document.getElementById('count-caption') as HTMLElement;
const countHud = document.getElementById('count-hud') as HTMLElement;
const countMuteButton = document.getElementById('count-mute-button') as HTMLButtonElement;
const countMuteIcon = document.getElementById('count-mute-icon') as HTMLImageElement;
const countHearButton = document.getElementById('count-hear-button') as HTMLButtonElement;
const countCompleteOverlay = document.getElementById('count-complete-overlay') as HTMLElement;
const countReplayButton = document.getElementById('count-replay-button') as HTMLButtonElement;
const countBackButton = document.getElementById('count-back-button') as HTMLButtonElement;
const countNextButton = document.getElementById('count-next-button') as HTMLButtonElement;

const fuelScreen = document.getElementById('fuel-screen') as HTMLElement;
const fuelStarsLayer = document.getElementById('fuel-stars-layer') as HTMLElement;
const fuelNumeral = document.getElementById('fuel-numeral') as HTMLElement;
const fuelTotal = document.getElementById('fuel-total') as HTMLElement;
const fuelRocket = document.getElementById('fuel-rocket') as HTMLElement;
const fuelSlots = document.getElementById('fuel-slots') as HTMLElement;
const fuelCountdown = document.getElementById('fuel-countdown') as HTMLElement;
const fuelCaption = document.getElementById('fuel-caption') as HTMLElement;
const fuelHud = document.getElementById('fuel-hud') as HTMLElement;
const fuelMuteButton = document.getElementById('fuel-mute-button') as HTMLButtonElement;
const fuelMuteIcon = document.getElementById('fuel-mute-icon') as HTMLImageElement;
const fuelHearButton = document.getElementById('fuel-hear-button') as HTMLButtonElement;
const fuelCompleteOverlay = document.getElementById('fuel-complete-overlay') as HTMLElement;
const fuelReplayButton = document.getElementById('fuel-replay-button') as HTMLButtonElement;
const fuelBackButton = document.getElementById('fuel-back-button') as HTMLButtonElement;

// Service worker install (prod) kicks off in parallel with the boot fetches.
// The READY gate independently confirms the precache via CacheStorage polling,
// so main.ts no longer races a fixed timeout.
void installServiceWorker();

initBootGate(
  { loadingOverlay, retryButton, startScreen, startButton },
  {
    onReady: () => {
      loadingOverlay.hidden = true;
      startScreen.hidden = false;
    },
    onStart: () => {
      void start();
    },
  },
);

async function start() {
  // PRP AC-02: never attempt audio before this gesture. The user gesture here
  // unlocks both HTMLAudioElement playback and (where supported) fullscreen.
  try {
    if (document.documentElement.requestFullscreen) {
      void document.documentElement.requestFullscreen().catch(() => {
        /* fail silently — PRP §Screen Flow */
      });
    }
  } catch {
    /* ignore */
  }

  let initialMuted = false;
  try {
    initialMuted = localStorage.getItem(CONFIG.muteStorageKey) === '1';
  } catch {
    /* localStorage unavailable */
  }

  // Construct Narration synchronously from lines already fetched during the
  // boot gate. No network await between the gesture and the first play() —
  // this keeps play('intro') inside the user-gesture tick (iOS audio unlock).
  const narration = new Narration(
    getCachedNarrationLines(),
    {
      initialMuted,
      onCaption: (text) => {
        caption.textContent = text;
        countCaption.textContent = text;
        fuelCaption.textContent = text;
      },
    },
    getCachedVoiceManifest(),
  );

  const syncMuteIcons = (muted: boolean) => {
    const src = muted ? '/art/icon-speaker-off.svg' : '/art/icon-speaker-on.svg';
    muteIcon.src = src;
    countMuteIcon.src = src;
    fuelMuteIcon.src = src;
  };

  syncMuteIcons(initialMuted);

  let level1Started = false;

  const activity = new Activity(
    {
      scene,
      orbits,
      silhouettes,
      planetsLayer,
      dock,
      caption,
      sun,
      muteButton,
      replayInstructionButton,
      completionOverlay,
      replayButton,
      muteIcon,
    },
    narration,
  );

  const countingActivity = new CountingActivity(
    {
      screen: countingScreen,
      starsLayer: countStarsLayer,
      numeral: countNumeral,
      total: countTotal,
      rocket: countRocket,
      caption: countCaption,
      hud: countHud,
      muteButton: countMuteButton,
      muteIcon: countMuteIcon,
      hearButton: countHearButton,
      completeOverlay: countCompleteOverlay,
      replayButton: countReplayButton,
      backButton: countBackButton,
      level1MuteIcon: muteIcon,
    },
    narration,
    {
      onBack: () => {
        syncMuteIcons(narration.muted_);
        activityScreen.hidden = false;
        if (level1Started) {
          void activity.replay();
        } else {
          level1Started = true;
          void activity.begin();
        }
      },
    },
  );

  const fuelActivity = new FuelActivity(
    {
      screen: fuelScreen,
      starsLayer: fuelStarsLayer,
      numeral: fuelNumeral,
      total: fuelTotal,
      rocket: fuelRocket,
      slots: fuelSlots,
      countdown: fuelCountdown,
      caption: fuelCaption,
      hud: fuelHud,
      muteButton: fuelMuteButton,
      muteIcon: fuelMuteIcon,
      hearButton: fuelHearButton,
      completeOverlay: fuelCompleteOverlay,
      replayButton: fuelReplayButton,
      backButton: fuelBackButton,
      level1MuteIcon: muteIcon,
    },
    narration,
    {
      onBack: () => {
        syncMuteIcons(narration.muted_);
        fuelScreen.hidden = true;
        countingScreen.hidden = false;
        void countingActivity.begin();
      },
    },
  );

  countNextButton.addEventListener('click', () => {
    countingActivity.suspend();
    syncMuteIcons(narration.muted_);
    fuelScreen.hidden = false;
    void fuelActivity.begin();
  });

  nextLevelButton.addEventListener('click', () => {
    activity.suspend();
    activityScreen.hidden = true;
    syncMuteIcons(narration.muted_);
    countingScreen.hidden = false;
    void countingActivity.begin();
  });

  startScreen.hidden = true;
  if (location.hash === '#fuel') {
    fuelScreen.hidden = false;
    void fuelActivity.begin();
  } else if (location.hash === '#count') {
    countingScreen.hidden = false;
    void countingActivity.begin();
  } else {
    activityScreen.hidden = false;
    level1Started = true;
    // activity.begin plays the intro line first; this is the first audible
    // play() and it runs inside the start-gesture tick.
    await activity.begin();
  }
}
